import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DictionaryProvider } from '../contexts/DictionaryContext';

import AuthPage from '../pages/AuthPage';
import VinculoSelectionPage from '../pages/VinculoSelectionPage';
import Loader from '../components/common/Loader';
import Layout from '../components/layout/Layout';

const lazyLoad = (importer) => {
  return lazy(() => importer().then(module => {
    return { default: module.default || Object.values(module)[0] };
  }));
};

const Dashboard = lazyLoad(() => import('../pages/Dashboard'));
const ChangePasswordPage = lazyLoad(() => import('../pages/ChangePasswordPage'));
const RecadastramentoPage = lazyLoad(() => import('../pages/RecadastramentoPage'));
const ContrachequePage = lazyLoad(() => import('../pages/ContrachequePage'));
const RequerimentosPage = lazyLoad(() => import('../pages/RequerimentosPage'));
const HelpCenterPage = lazyLoad(() => import('../pages/HelpCenterPage'));
const EntendaSeuCalculoPage = lazyLoad(() => import('../pages/EntendaSeuCalculoPage'));

// 🔹 ADIÇÃO: Importação da nova página de Avisos
const AvisosPage = lazyLoad(() => import('../pages/AvisosPage'));


const ProtectedRoute = ({ children, requireVinculo = true }) => {
  const { isAuthenticated, loading, user, vinculos, vinculoSelecionado } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><Loader /></div>;
  }

  if (!isAuthenticated) {
    return <Navigate to={`/login${location.search}`} replace />;
  }
  
  // Redireciona para change-password APENAS se for obrigatório (primeiro acesso)
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  const hasMultipleVinculos = vinculos && vinculos.length > 1;
  const hasSelectedVinculo = !!vinculoSelecionado;

  if (requireVinculo) {
    if (hasMultipleVinculos && !hasSelectedVinculo) {
      if (location.pathname !== '/selecionar-vinculo') {
        return <Navigate to="/selecionar-vinculo" replace />;
      }
    }
  }

  if (hasSelectedVinculo && location.pathname === '/selecionar-vinculo') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};


const AppRouter = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const municipioIdFromUrl = params.get('municipioId');
    if (municipioIdFromUrl) {
      localStorage.setItem('municipioId', municipioIdFromUrl);
    }
  }, [location.search]);

  return (
    <DictionaryProvider>
      <Suspense fallback={<div className="flex justify-center items-center h-screen"><Loader /></div>}>
        <Routes>
          <Route path="/login" element={<AuthPage />} />
          
          <Route 
            path="/change-password" 
            element={<ProtectedRoute requireVinculo={false}><ChangePasswordPage /></ProtectedRoute>} 
          />
          <Route 
            path="/selecionar-vinculo" 
            element={<ProtectedRoute requireVinculo={false}><VinculoSelectionPage /></ProtectedRoute>} 
          />

          <Route 
            path="/dashboard" 
            element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} 
          />
          <Route 
            path="/recadastramento" 
            element={<ProtectedRoute><Layout><RecadastramentoPage /></Layout></ProtectedRoute>} 
          />
          
          <Route 
            path="/contracheques/:matricula" 
            element={<ProtectedRoute><Layout><ContrachequePage /></Layout></ProtectedRoute>} 
          />
          
          <Route 
            path="/contracheques/:matricula/calculo" 
            element={<ProtectedRoute><Layout><EntendaSeuCalculoPage /></Layout></ProtectedRoute>} 
          />
          
          <Route 
            path="/requerimentos" 
            element={<ProtectedRoute><Layout><RequerimentosPage /></Layout></ProtectedRoute>} 
          />

          {/* 🔹 ADIÇÃO: Rota para a página de Avisos */}
          <Route 
            path="/avisos" 
            element={<ProtectedRoute><Layout><AvisosPage /></Layout></ProtectedRoute>} 
          />
          
          <Route 
            path="/ajuda" 
            element={<ProtectedRoute><Layout><HelpCenterPage /></Layout></ProtectedRoute>} 
          />
          
          <Route 
            path="/"
            element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </DictionaryProvider>
  );
};

export default AppRouter;